import { Typography } from "@mui/material";
import DropdownMenu from "components/common/DropdownMenu";
import {
  Copy,
  Phone as PhoneIcon,
  MessageSquare,
  Map,
  Mail,
} from "lucide-react";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import { useNavigate } from "react-router-dom";
import { ReactNode } from "react";
import { Family } from "protogen/advisors_service_pb";
import { Member } from "protogen/common_pb";
import { getFormattedLocation } from "../utils";
import { formatUSPhoneNumber } from "common/utils";
import FlexBox from "components/common/FlexBox";

const ValueMenu = ({
  value,
  rawValue,
  content,
  copy = true,
  call,
  text,
  email,
  map,
}: {
  value: string;
  rawValue?: string;
  content?: ReactNode;
  copy?: boolean;
  call?: boolean;
  text?: boolean;
  email?: boolean;
  map?: boolean;
}) => {
  const { pushSnack } = useSnackpack();
  const navigate = useNavigate();
  const onCopy = () => {
    navigator.clipboard.writeText(value);
    pushSnack("Copied to clipboard");
  };
  if (!value) {
    return null;
  }
  return (
    <DropdownMenu
      disabled={false}
      buttonContent={content || <Typography variant="body">{value}</Typography>}
      sx={{
        color: "unset",
        minWidth: "auto",
        height: "unset",
        alignItems: "start",
        justifyContent: "start",
        textAlign: "start",
      }}
      dropdownIcon={false}
      variant="text"
      anchorToCursor
      options={[
        ...(copy
          ? [
              {
                title: "Copy",
                onClick: onCopy,
                IconComponent: Copy,
              },
            ]
          : []),
        ...(call
          ? [
              {
                title: "Call",
                onClick: () => {
                  navigate(
                    `/inbox/phone/create?phone=${encodeURIComponent(
                      rawValue || value,
                    )}`,
                  );
                },
                IconComponent: PhoneIcon,
              },
            ]
          : []),
        ...(text
          ? [
              {
                title: "Text",
                onClick: () =>
                  navigate(
                    `/inbox/phone/create?phone=${encodeURIComponent(value)}`,
                  ),
                IconComponent: MessageSquare,
              },
            ]
          : []),
        ...(email
          ? [
              {
                title: "Email",
                onClick: () =>
                  navigate(
                    `/inbox/email/create?email=${encodeURIComponent(value)}`,
                  ),
                IconComponent: Mail,
              },
            ]
          : []),
        ...(map
          ? [
              {
                title: "Map",
                onClick: () =>
                  window.open(
                    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      value,
                    )}`,
                    "_blank",
                  ),
                IconComponent: Map,
              },
            ]
          : []),
      ]}
    />
  );
};

const MemberSection = ({ member }: { member: Member }) => (
  <FlexBox gap="2px" direction="column">
    <Typography variant="bodyHeavy">
      {member.firstName} {member.lastName}
    </Typography>
    <ValueMenu
      rawValue={member.primaryPhone}
      value={formatUSPhoneNumber(member.primaryPhone)}
      call={true}
      text={true}
      content={
        <Typography variant="body">
          {formatUSPhoneNumber(member.primaryPhone)}
        </Typography>
      }
    />
    <ValueMenu value={member.primaryEmail} email={true} />
    {member.alternateEmails.map((email) => (
      <ValueMenu key={email} value={email} email={true} />
    ))}
  </FlexBox>
);

export default ({ family, flex }: { family: Family; flex: number }) => {
  const address = getFormattedLocation(family.address).replace(", USA", "");
  return (
    <FlexBox
      variant="contained"
      direction="column"
      gap="4px"
      sx={{ flex: flex }}
    >
      <Typography variant="h4">Family details</Typography>
      <FlexBox direction="column">
        <ValueMenu
          value={address}
          map={true}
          content={<Typography variant="body">{address}</Typography>}
        />

        {family.familyMembers.map((m) => (
          <MemberSection key={m.ref} member={m} />
        ))}
      </FlexBox>
    </FlexBox>
  );
};
